import { useCallback, useMemo, useState } from "react";
import { ExpandedDetails } from "./LiveMonitorTable";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { COLORS } from "../assets/theme";
import { formatNumberWithCommas } from "../utils/helpers";

const NUMERIC_COLUMNS = new Set([
  "Count",
  "Delivered",
  "Rate",
  "Profit",
  "TotalRate",
  "TotalProfit",
]);

interface ExpandedDetailsTableProps {
  details: ExpandedDetails;
}

export const ExpandedDetailsTable: React.FC<ExpandedDetailsTableProps> = ({
  details,
}) => {
  const [sort, setSort] = useState<{
    field: string;
    direction: "asc" | "desc";
  }>({
    field: "Count",
    direction: "desc",
  });

  const handleSort = useCallback((field: string) => {
    setSort((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const SORT_VALUE_KEYS: Record<string, string> = {
    "Converted Cost": "convertedCostValue",
    "Total Converted Cost": "totalConvertedCostValue",
  };

  const getSortValue = (row: any, column: string) => {
    const value = row[SORT_VALUE_KEYS[column] ?? column];
    if (value == null || value === "" || value === "-") return null;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // "12.34%" and other formatted numbers should sort numerically
      const numeric = Number(value.replace(/[%,]/g, "").trim());
      return isNaN(numeric) ? value : numeric;
    }
    return null;
  };

  const sortedRows = useMemo(() => {
    if (!sort.field) return details.rows;
    const { field, direction } = sort;
    return [...details.rows].sort((a, b) => {
      const aVal = getSortValue(a, field);
      const bVal = getSortValue(b, field);
      if (aVal == null)
        return bVal != null ? (direction === "asc" ? -1 : 1) : 0;
      if (bVal == null) return direction === "asc" ? 1 : -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [details.rows, sort]);

  return (
    <>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {details.columns.map((col) => {
                const isSortable = col !== "";
                return (
                  <TableCell
                    key={col}
                    onClick={isSortable ? () => handleSort(col) : undefined}
                    sx={{
                      fontWeight: "600",
                      cursor: isSortable ? "pointer" : "default",
                    }}
                  >
                    {col}
                    {isSortable && sort.field === col && (
                      <>
                        {sort.direction === "asc" ? (
                          <ArrowUpward sx={{ fontSize: 15, ml: 0.5 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 15, ml: 0.5 }} />
                        )}
                      </>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {details.isLoading ? (
              <TableRow>
                <TableCell colSpan={details.columns.length} align="center">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    py={3}
                  >
                    <CircularProgress
                      size={28}
                      color="inherit"
                      sx={{ color: COLORS.GOLD }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : details.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={details.columns.length} align="center">
                  <Typography variant="body2">No details</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#e0e0e0" : "#d6d6d6",
                  }}
                >
                  {details.columns.map((column) => {
                    const value = row[column];
                    const display =
                      NUMERIC_COLUMNS.has(column) && typeof value === "number"
                        ? formatNumberWithCommas(value, {
                            maximumFractionDigits: 6,
                          })
                        : value;
                    return <TableCell key={column}>{display}</TableCell>;
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
